import { defineStore } from 'pinia';
import { AppMode, KIOSK_TOKEN_HEADER, Right, type Kiosk } from '@taxi/shared';
import { ApiException, useTokenStore, useUserStore } from '@common/index';

const KIOSK_TOKEN_KEY = 'taxi_kiosk_token';
const MODE_PREFERENCE_KEY = 'taxi_mode_preference';

export interface AppModeState {
	mode: AppMode;
	kiosk: Kiosk | null;
	/**
	 * Vrai quand un jeton de borne fraîchement fourni a été refusé.
	 *
	 * Consommé par l'accueil, qui le signale une fois : sans cela, une URL de
	 * borne périmée donnerait l'écran client ordinaire, indiscernable d'un lien
	 * qui ne fonctionne pas.
	 */
	kioskRejected: boolean;
	/** Vrai tant que le mode n'est pas déterminé — évite d'afficher le mauvais écran. */
	resolving: boolean;
}

/**
 * Vrai dès qu'un jeton de borne est mémorisé, avant même que le mode soit résolu.
 *
 * La garde de navigation s'exécute à la toute première navigation, bien avant
 * que `resolve()` ait interrogé l'API : s'en remettre au mode du store laisserait
 * passer une adresse saisie à la main — `/connexion` sur une borne, précisément
 * ce qu'on veut interdire.
 */
export const storedKioskToken = (): string | null =>
	window.localStorage.getItem(KIOSK_TOKEN_KEY);

export const hasStoredKioskToken = (): boolean => !!storedKioskToken();

/**
 * En-tête d'appareil joint à chaque requête d'une borne.
 *
 * Le JWT porte déjà l'identifiant de la borne, mais il voyage seul : recopié
 * ailleurs, il y vaudrait autant que sur l'écran mural — et il ouvre le droit de
 * commander. L'API exige donc que les deux désignent la même borne.
 */
export const kioskDeviceHeaders = (): Record<string, string> => {
	const token = storedKioskToken();
	return token ? { [KIOSK_TOKEN_HEADER]: token } : {};
};

/**
 * Mode d'affichage de l'application.
 *
 * Trois visages pour un même code :
 *  - **borne** — déclenché par `?kiosk=<jeton>` dans l'URL, ou par un jeton de
 *    borne déjà mémorisé. La position vient de la base, jamais du GPS ;
 *  - **conduite** — écran d'accueil par défaut pour un compte chauffeur ;
 *  - **client** — tout le reste.
 *
 * Un chauffeur peut basculer manuellement en mode client depuis le menu : son
 * choix est mémorisé, sans quoi il retrouverait l'écran de conduite à chaque
 * ouverture.
 */
export const useAppModeStore = defineStore('appMode', {
	state: (): AppModeState => ({
		mode: AppMode.CLIENT,
		kiosk: null,
		kioskRejected: false,
		resolving: true,
	}),

	getters: {
		isKiosk: (state): boolean => state.mode === AppMode.KIOSK,
		isDriving: (state): boolean => state.mode === AppMode.DRIVER,
		/** Position fixe de la borne : elle n'a aucun capteur à interroger. */
		kioskPosition: (state) => state.kiosk?.position ?? null,
	},

	actions: {
		/**
		 * Détermine le mode au démarrage.
		 *
		 * L'ordre compte : la borne prime sur tout le reste. Un écran mural ne doit
		 * jamais basculer en mode conduite ou client parce qu'un compte traîne dans
		 * le stockage local.
		 */
		async resolve(): Promise<AppMode> {
			this.resolving = true;
			try {
				// Prise d'identité depuis le back-office : le jeton arrive dans le
				// fragment, qui n'est ni transmis au serveur ni conservé dans ses
				// journaux d'accès — contrairement à une query string.
				this.consumeImpersonationToken();

				const params = new URLSearchParams(window.location.search);
				const urlToken = params.get('kiosk');

				// `?kiosk=off` quitte le mode borne.
				//
				// Le jeton est délibérément persistant : une borne redémarre et doit
				// revenir d'elle-même en mode borne, sans qu'on aille ressaisir son
				// URL. Mais sans porte de sortie, un navigateur ayant servi une fois
				// de borne le resterait indéfiniment — y compris un poste de test.
				if (urlToken === 'off' || urlToken === '') {
					this.exitKiosk();
					this.stripKioskParam(params);
					this.mode = AppMode.CLIENT;
					return this.mode;
				}

				if (urlToken) {
					await this.startKiosk(urlToken, true);
					this.stripKioskParam(params);
					return this.mode;
				}

				const storedToken = window.localStorage.getItem(KIOSK_TOKEN_KEY);
				if (storedToken) {
					await this.startKiosk(storedToken);
					return this.mode;
				}

				const userStore = useUserStore();
				const tokenStore = useTokenStore();

				if (tokenStore.isKiosk) {
					await this.loadKiosk();
					this.mode = AppMode.KIOSK;
					return this.mode;
				}

				const canDrive = tokenStore.rights.includes(Right.DRIVER_MODE);
				const preference = window.localStorage.getItem(MODE_PREFERENCE_KEY) as AppMode | null;

				if (canDrive && userStore.isDriver) {
					this.mode = preference === AppMode.CLIENT ? AppMode.CLIENT : AppMode.DRIVER;
				} else {
					this.mode = AppMode.CLIENT;
				}

				return this.mode;
			} finally {
				this.resolving = false;
			}
		},

		/**
		 * Retire le jeton de la barre d'adresse une fois consommé.
		 *
		 * Inutile de le laisser visible sur un écran public, et cela évite qu'il
		 * finisse dans un historique ou une capture d'écran.
		 */
		stripKioskParam(params: URLSearchParams): void {
			params.delete('kiosk');
			const query = params.toString();
			window.history.replaceState({}, '', window.location.pathname + (query ? `?${query}` : ''));
		},

		/**
		 * Consomme un jeton de prise d'identité présent dans le fragment d'URL.
		 *
		 * Le fragment est effacé aussitôt : laisser un jeton d'accès dans la barre
		 * d'adresse le rendrait copiable, partageable et récupérable dans
		 * l'historique du navigateur.
		 */
		consumeImpersonationToken(): boolean {
			const fragment = window.location.hash.replace(/^#/, '');
			if (!fragment.startsWith('impersonate=')) {
				return false;
			}

			const accessToken = decodeURIComponent(fragment.slice('impersonate='.length));
			if (!accessToken) {
				return false;
			}

			try {
				// Le jeton porte déjà rôles et droits : on reconstitue la session sans
				// solliciter l'API, qui les revérifiera à chaque appel de toute façon.
				const payload = JSON.parse(atob(accessToken.split('.')[1])) as {
					sub: number;
					roles: string[];
					rights: string[];
					exp: number;
					guest?: boolean;
				};

				useTokenStore().setToken({
					accessToken,
					refreshToken: null,
					expiresAt: new Date(payload.exp * 1000).toISOString(),
					roles: payload.roles as never,
					rights: payload.rights as never,
					userId: payload.sub,
					kioskId: null,
					isGuest: payload.guest ?? false,
				});
			} catch {
				return false;
			} finally {
				window.history.replaceState({}, '', window.location.pathname + window.location.search);
			}

			return true;
		},

		/** Quitte le mode borne et oublie le jeton mémorisé. */
		exitKiosk(): void {
			window.localStorage.removeItem(KIOSK_TOKEN_KEY);
			this.kiosk = null;
			useTokenStore().setToken(null);
		},

		/**
		 * Ouvre la session machine d'une borne à partir de son jeton d'URL.
		 *
		 * Un échec ne remonte pas d'erreur — un voyageur devant une borne mal
		 * provisionnée n'a que faire d'un message technique — mais il est **noté** :
		 * sans cela, ouvrir une URL de borne périmée donnerait l'écran client
		 * ordinaire, et rien ne dirait que le jeton a été refusé. C'est
		 * indiscernable d'un lien qui « ne marche pas ».
		 */
		async startKiosk(token: string, fromUrl = false): Promise<void> {
			const userStore = useUserStore();
			try {
				await userStore.kioskSession(token);
				window.localStorage.setItem(KIOSK_TOKEN_KEY, token);
				await this.loadKiosk();
				this.mode = AppMode.KIOSK;
				this.kioskRejected = false;
			} catch (error) {
				const status = error instanceof ApiException ? error.status : 0;
				// Jeton révoqué ou borne désactivée : l'API le dit explicitement, on
				// purge pour ne pas boucler à chaque ouverture.
				//
				// Tout le reste — API en cours de redémarrage, coupure réseau, 502 du
				// proxy — est passager : **on garde le jeton**. L'oublier
				// condamnerait l'écran à rester en mode client jusqu'à ce que
				// quelqu'un retourne saisir son URL de provisionnement, pour une
				// panne de quelques secondes.
				const revoked = status === 401 || status === 403;

				if (revoked) {
					this.exitKiosk();
				} else {
					// Session non ouverte : le jeton reste, la prochaine tentative
					// (réouverture, retour d'arrière-plan) reprendra.
					useTokenStore().setToken(null);
					this.kiosk = null;
				}

				this.mode = AppMode.CLIENT;
				// Seul un jeton fraîchement collé et explicitement refusé mérite d'être
				// signalé : un jeton mémorisé se purge en silence, l'écran ayant pu
				// changer de main depuis.
				this.kioskRejected = fromUrl && revoked;
			}
		},

		async loadKiosk(): Promise<void> {
			this.kiosk = await this.$caller().get<Kiosk>('/kiosks/me');
		},

		/** Bascule chauffeur ⇄ client, à l'initiative de l'utilisateur. */
		switchMode(mode: AppMode): void {
			if (this.isKiosk) {
				return;
			}
			this.mode = mode;
			window.localStorage.setItem(MODE_PREFERENCE_KEY, mode);
		},

		/**
		 * Reprend le mode borne après une panne passagère.
		 *
		 * Un jeton conservé mais sans session ouverte signifie que la tentative
		 * précédente a échoué pour une raison sans rapport avec lui — API en cours
		 * de redémarrage, réseau coupé. L'écran doit se rattraper tout seul : sur
		 * une borne murale, personne ne viendra recharger la page.
		 */
		async retryKiosk(): Promise<void> {
			if (this.isKiosk || this.resolving) {
				return;
			}
			const token = window.localStorage.getItem(KIOSK_TOKEN_KEY);
			if (token) {
				await this.startKiosk(token);
			}
		},

		/** Signal de vie de la borne : permet au back-office de repérer un écran figé. */
		async heartbeat(): Promise<void> {
			if (!this.isKiosk) {
				return;
			}
			try {
				this.kiosk = await this.$caller().post<Kiosk>('/kiosks/me/heartbeat');
			} catch {
				// Sans conséquence : la borne reste utilisable hors connexion brève.
			}
		},
	},
});
